import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEnvelope,
  faPhone,
  faBuilding,
  faUser,
  faCalendar,
  faEye,
  faEyeSlash,
  faEdit,
  faTrash,
  faSearch,
  faFilter,
  faDownload,
  faExclamationTriangle,
  faCheckCircle,
  faClock,
  faFlag,
  faComment,
  faTasks,
  faSort,
  faChevronDown,
  faChevronUp,
  faDollarSign,
  faIndustry,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { contactAPI } from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const ContactManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [expandedContact, setExpandedContact] = useState(null);
  const [showRemarkModal, setShowRemarkModal] = useState(false);
  const [remarkTarget, setRemarkTarget] = useState(null);
  const [remarkText, setRemarkText] = useState('');
  
  // 筛选和搜索状态
  const [filters, setFilters] = useState({
    search: '',
    type: '',
    status: '',
    priority: '',
    isRead: '',
    sortBy: 'createdAt',
    sortOrder: 'desc'
  });
  
  // 分页状态
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 10
  });

  // 获取联系记录列表
  useEffect(() => {
    fetchContacts();
  }, [filters, pagination.currentPage]);

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        search: filters.search,
        type: filters.type,
        status: filters.status,
        priority: filters.priority,
        isRead: filters.isRead,
        sortBy: filters.sortBy,
        sortOrder: filters.sortOrder
      };
      
      // 清除空值
      Object.keys(params).forEach(key => {
        if (params[key] === '' || params[key] === null || params[key] === undefined) {
          delete params[key];
        }
      });
      
      const response = await contactAPI.getList(params);
      if (response.data.status === 'success') {
        setContacts(response.data.data.contacts);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.data.pagination.totalPages,
          totalItems: response.data.data.pagination.totalItems
        }));
      } else {
        setError(response.data.message || '获取联系记录失败');
      }
    } catch (err) {
      setError(err.message || '获取联系记录失败');
    } finally {
      setLoading(false);
    }
  };

  // 处理搜索
  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchContacts();
  };

  // 处理筛选变化
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  };

  // 处理排序
  const handleSort = (field) => {
    const newOrder = filters.sortBy === field && filters.sortOrder === 'desc' ? 'asc' : 'desc';
    setFilters(prev => ({
      ...prev,
      sortBy: field,
      sortOrder: newOrder
    }));
  };

  // 处理选择
  const handleSelectContact = (contactId) => {
    setSelectedContacts(prev => {
      if (prev.includes(contactId)) {
        return prev.filter(id => id !== contactId);
      } else {
        return [...prev, contactId];
      }
    });
  };

  // 全选/取消全选
  const handleSelectAll = () => {
    if (selectedContacts.length === contacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contacts.map(item => item._id));
    }
  };

  // 标记已读/未读
  const handleMarkRead = async (contactId, isRead) => {
    try {
      const response = await contactAPI.update(contactId, { isRead });
      if (response.data.status === 'success') {
        setContacts(prev => prev.map(item => 
          item._id === contactId ? { ...item, isRead } : item
        ));
      }
    } catch (err) {
      alert('操作失败');
    }
  };

  // 更新状态
  const handleUpdateStatus = async (contactId, status) => {
    try {
      const response = await contactAPI.update(contactId, { status });
      if (response.data.status === 'success') {
        setContacts(prev => prev.map(item => 
          item._id === contactId ? { ...item, status, statusLabel: getStatusText(status) } : item
        ));
      }
    } catch (err) {
      alert('更新状态失败');
    }
  };

  // 更新优先级
  const handleUpdatePriority = async (contactId, priority) => {
    try {
      const response = await contactAPI.update(contactId, { priority });
      if (response.data.status === 'success') {
        setContacts(prev => prev.map(item => 
          item._id === contactId ? { ...item, priority, priorityLabel: getPriorityText(priority) } : item
        ));
      }
    } catch (err) {
      alert('更新优先级失败');
    }
  };

  // 添加备注
  const handleAddRemark = async () => {
    if (!remarkText.trim()) return;
    
    try {
      const response = await contactAPI.addRemark(remarkTarget._id, { content: remarkText });
      if (response.data.status === 'success') {
        setContacts(prev => prev.map(item => 
          item._id === remarkTarget._id 
            ? { ...item, remarks: [...(item.remarks || []), response.data.data] }
            : item
        ));
        setShowRemarkModal(false);
        setRemarkTarget(null);
        setRemarkText('');
      }
    } catch (err) {
      alert('添加备注失败');
    }
  };

  // 删除联系记录
  const handleDelete = async (contactId) => {
    try {
      const response = await contactAPI.delete(contactId);
      if (response.data.status === 'success') {
        setContacts(prev => prev.filter(item => item._id !== contactId));
        setSelectedContacts(prev => prev.filter(id => id !== contactId));
        setShowDeleteModal(false);
        setDeleteTarget(null);
      } else {
        alert(response.data.message || '删除失败');
      }
    } catch (err) {
      alert(err.message || '删除失败');
    }
  };

  // 批量删除
  const handleBatchDelete = async () => {
    if (selectedContacts.length === 0) return;
    
    if (window.confirm(`确定要删除选中的 ${selectedContacts.length} 条记录吗？`)) {
      try {
        await Promise.all(selectedContacts.map(id => contactAPI.delete(id)));
        setContacts(prev => prev.filter(item => !selectedContacts.includes(item._id)));
        setSelectedContacts([]);
      } catch (err) {
        alert('批量删除失败');
      }
    }
  };

  // 导出数据
  const handleExport = async () => {
    try {
      const response = await contactAPI.export(filters);
      // 创建下载链接
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `contacts_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert('导出失败');
    }
  };

  // 获取咨询类型样式
  const getTypeStyle = (type) => {
    switch (type) {
      case 'product':
        return 'bg-blue-100 text-blue-800';
      case 'service':
        return 'bg-green-100 text-green-800';
      case 'support':
        return 'bg-yellow-100 text-yellow-800';
      case 'partnership':
        return 'bg-purple-100 text-purple-800';
      case 'other':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 获取状态样式
  const getStatusStyle = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'processing':
        return 'bg-blue-100 text-blue-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'closed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 获取优先级样式
  const getPriorityStyle = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  // 获取状态文本
  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return '待处理';
      case 'processing':
        return '处理中';
      case 'completed':
        return '已完成';
      case 'closed':
        return '已关闭';
      default:
        return '未知';
    }
  };

  // 获取优先级文本
  const getPriorityText = (priority) => {
    switch (priority) {
      case 'high':
        return '高';
      case 'medium':
        return '中';
      case 'low':
        return '低';
      default:
        return '未设置';
    }
  };

  if (loading && contacts.length === 0) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* 页面标题和操作 */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">联系管理</h1>
            <p className="mt-1 text-sm text-gray-500">
              共 {pagination.totalItems} 条咨询记录
            </p>
          </div>
          
          <div className="flex items-center space-x-3">
            {selectedContacts.length > 0 && (
              <button
                onClick={handleBatchDelete}
                className="inline-flex items-center px-4 py-2 border border-red-300 text-sm font-medium rounded-md text-red-700 bg-red-50 hover:bg-red-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
              >
                <FontAwesomeIcon icon={faTrash} className="mr-2" />
                删除选中 ({selectedContacts.length})
              </button>
            )}
            
            <button
              onClick={handleExport}
              className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <FontAwesomeIcon icon={faDownload} className="mr-2" />
              导出数据
            </button>
          </div>
        </div>

        {/* 搜索和筛选 */}
        <div className="bg-white rounded-lg shadow p-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {/* 搜索框 */}
              <div className="md:col-span-2">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FontAwesomeIcon icon={faSearch} className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="搜索姓名、公司、邮箱或电话..."
                  />
                </div>
              </div>
              
              {/* 咨询类型筛选 */}
              <div>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">所有类型</option>
                  <option value="product">产品咨询</option>
                  <option value="service">服务咨询</option>
                  <option value="support">技术支持</option>
                  <option value="partnership">合作洽谈</option>
                  <option value="other">其他</option>
                </select>
              </div>
              
              {/* 状态筛选 */}
              <div>
                <select
                  value={filters.status}
                  onChange={(e) => handleFilterChange('status', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">所有状态</option>
                  <option value="pending">待处理</option>
                  <option value="processing">处理中</option>
                  <option value="completed">已完成</option>
                  <option value="closed">已关闭</option>
                </select>
              </div>
              
              {/* 优先级筛选 */}
              <div>
                <select
                  value={filters.priority}
                  onChange={(e) => handleFilterChange('priority', e.target.value)}
                  className="block w-full px-3 py-2 border border-gray-300 rounded-md leading-5 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">所有优先级</option>
                  <option value="high">高</option>
                  <option value="medium">中</option>
                  <option value="low">低</option>
                </select>
              </div>
            </div>
            
            <div className="flex justify-between items-center">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <FontAwesomeIcon icon={faSearch} className="mr-2" />
                搜索
              </button>
              
              <button
                type="button"
                onClick={() => {
                  setFilters({
                    search: '',
                    type: '',
                    status: '',
                    priority: '',
                    isRead: '',
                    sortBy: 'createdAt',
                    sortOrder: 'desc'
                  });
                  setPagination(prev => ({ ...prev, currentPage: 1 }));
                }}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                重置筛选
              </button>
            </div>
          </form>
        </div>

        {/* 联系记录列表 */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {error ? (
            <div className="p-6">
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <FontAwesomeIcon icon={faExclamationTriangle} className="h-5 w-5 text-red-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">加载失败</h3>
                    <p className="mt-1 text-sm text-red-700">{error}</p>
                    <button
                      onClick={fetchContacts}
                      className="mt-2 text-sm text-red-800 underline hover:text-red-900"
                    >
                      重试
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* 表格头部 */}
              <div className="px-6 py-3 bg-gray-50 border-b border-gray-200">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={selectedContacts.length === contacts.length && contacts.length > 0}
                    onChange={handleSelectAll}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <span className="ml-3 text-sm font-medium text-gray-700">全选</span>
                </div>
              </div>
              
              {/* 表格内容 */}
              <div className="divide-y divide-gray-200">
                {contacts.length > 0 ? (
                  contacts.map((item) => (
                    <div key={item._id} className={`px-6 py-4 hover:bg-gray-50 ${!item.isRead ? 'bg-blue-50' : ''}`}>
                      <div className="flex items-start space-x-4">
                        {/* 选择框 */}
                        <input
                          type="checkbox"
                          checked={selectedContacts.includes(item._id)}
                          onChange={() => handleSelectContact(item._id)}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mt-1"
                        />
                        
                        {/* 联系信息 */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              {/* 基本信息 */}
                              <div className="flex items-center space-x-3 mb-2">
                                <h3 className="text-lg font-medium text-gray-900">
                                  {item.name}
                                  {!item.isRead && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                      新
                                    </span>
                                  )}
                                </h3>
                                
                                {/* 咨询类型 */}
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeStyle(item.type)}`}>
                                  {item.typeLabel}
                                </span>
                                
                                {/* 状态 */}
                                <select
                                  value={item.status}
                                  onChange={(e) => handleUpdateStatus(item._id, e.target.value)}
                                  className={`text-xs font-medium rounded-full px-2.5 py-0.5 border-0 ${getStatusStyle(item.status)}`}
                                >
                                  <option value="pending">待处理</option>
                                  <option value="processing">处理中</option>
                                  <option value="completed">已完成</option>
                                  <option value="closed">已关闭</option>
                                </select>
                                
                                {/* 优先级 */}
                                <select
                                  value={item.priority}
                                  onChange={(e) => handleUpdatePriority(item._id, e.target.value)}
                                  className={`text-xs font-medium rounded-full px-2.5 py-0.5 border-0 ${getPriorityStyle(item.priority)}`}
                                >
                                  <option value="low">低</option>
                                  <option value="medium">中</option>
                                  <option value="high">高</option>
                                </select>
                              </div>
                              
                              {/* 联系方式 */}
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-600 mb-2">
                                <div className="flex items-center">
                                  <FontAwesomeIcon icon={faEnvelope} className="mr-2 text-gray-400" />
                                  <a href={`mailto:${item.email}`} className="hover:text-blue-600">
                                    {item.email}
                                  </a>
                                </div>
                                <div className="flex items-center">
                                  <FontAwesomeIcon icon={faPhone} className="mr-2 text-gray-400" />
                                  <a href={`tel:${item.phone}`} className="hover:text-blue-600">
                                    {item.phone}
                                  </a>
                                </div>
                                {item.company && (
                                  <div className="flex items-center">
                                    <FontAwesomeIcon icon={faBuilding} className="mr-2 text-gray-400" />
                                    {item.company}
                                  </div>
                                )}
                                <div className="flex items-center">
                                  <FontAwesomeIcon icon={faCalendar} className="mr-2 text-gray-400" />
                                  {new Date(item.createdAt).toLocaleString()}
                                </div>
                              </div>
                              
                              {/* 需求描述 */}
                              <div className="text-sm text-gray-700 mb-3">
                                <p className="line-clamp-2">{item.message}</p>
                              </div>
                              
                              {/* 展开详情 */}
                              {expandedContact === item._id && (
                                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                                    {item.companySize && (
                                      <div>
                                        <span className="font-medium text-gray-700">公司规模：</span>
                                        {item.companySize}
                                      </div>
                                    )}
                                    {item.industry && (
                                      <div>
                                        <span className="font-medium text-gray-700">行业：</span>
                                        {item.industry}
                                      </div>
                                    )}
                                    {item.budget && (
                                      <div>
                                        <span className="font-medium text-gray-700">预算：</span>
                                        {item.budget}
                                      </div>
                                    )}
                                    {item.timeline && (
                                      <div>
                                        <span className="font-medium text-gray-700">时间线：</span>
                                        {item.timeline}
                                      </div>
                                    )}
                                    {item.source && (
                                      <div>
                                        <span className="font-medium text-gray-700">来源：</span>
                                        {item.source}
                                      </div>
                                    )}
                                    {item.assignedTo && (
                                      <div>
                                        <span className="font-medium text-gray-700">分配给：</span>
                                        {item.assignedToName}
                                      </div>
                                    )}
                                  </div>
                                  
                                  {/* 备注列表 */}
                                  {item.remarks && item.remarks.length > 0 && (
                                    <div className="mt-4">
                                      <h4 className="font-medium text-gray-700 mb-2">备注记录</h4>
                                      <div className="space-y-2">
                                        {item.remarks.map((remark, index) => (
                                          <div key={index} className="bg-white p-3 rounded border">
                                            <p className="text-sm text-gray-700">{remark.content}</p>
                                            <div className="mt-1 text-xs text-gray-500">
                                              {remark.createdBy} · {new Date(remark.createdAt).toLocaleString()}
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                            
                            {/* 操作按钮 */}
                            <div className="flex items-center space-x-2 ml-4">
                              <button
                                onClick={() => handleMarkRead(item._id, !item.isRead)}
                                className="text-gray-400 hover:text-gray-600"
                                title={item.isRead ? '标记未读' : '标记已读'}
                              >
                                <FontAwesomeIcon icon={item.isRead ? faEyeSlash : faEye} />
                              </button>
                              
                              <button
                                onClick={() => {
                                  setRemarkTarget(item);
                                  setShowRemarkModal(true);
                                }}
                                className="text-blue-400 hover:text-blue-600"
                                title="添加备注"
                              >
                                <FontAwesomeIcon icon={faComment} />
                              </button>
                              
                              <button
                                onClick={() => setExpandedContact(expandedContact === item._id ? null : item._id)}
                                className="text-gray-400 hover:text-gray-600"
                                title="展开详情"
                              >
                                <FontAwesomeIcon icon={expandedContact === item._id ? faChevronUp : faChevronDown} />
                              </button>
                              
                              <button
                                onClick={() => {
                                  setDeleteTarget(item);
                                  setShowDeleteModal(true);
                                }}
                                className="text-red-400 hover:text-red-600"
                                title="删除"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-12 text-center">
                    <FontAwesomeIcon icon={faEnvelope} className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">暂无联系记录</h3>
                    <p className="text-gray-500">还没有收到任何客户咨询</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {/* 分页 */}
        {pagination.totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 rounded-lg shadow">
            <div className="flex-1 flex justify-between sm:hidden">
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                disabled={pagination.currentPage === 1}
                className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                上一页
              </button>
              <button
                onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                disabled={pagination.currentPage === pagination.totalPages}
                className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                下一页
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  显示第 <span className="font-medium">{(pagination.currentPage - 1) * pagination.itemsPerPage + 1}</span> 到{' '}
                  <span className="font-medium">
                    {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                  </span>{' '}
                  条，共 <span className="font-medium">{pagination.totalItems}</span> 条记录
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }))}
                    disabled={pagination.currentPage === 1}
                    className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  
                  {/* 页码按钮 */}
                  {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <button
                        key={pageNum}
                        onClick={() => setPagination(prev => ({ ...prev, currentPage: pageNum }))}
                        className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                          pagination.currentPage === pageNum
                            ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                            : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                        }`}
                      >
                        {pageNum}
                      </button>
                    );
                  })}
                  
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }))}
                    disabled={pagination.currentPage === pagination.totalPages}
                    className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 删除确认模态框 */}
      {showDeleteModal && deleteTarget && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3 text-center">
              <FontAwesomeIcon icon={faExclamationTriangle} className="h-12 w-12 text-red-600 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">确认删除</h3>
              <p className="text-sm text-gray-500 mb-4">
                确定要删除来自「{deleteTarget.name}」的咨询记录吗？此操作不可撤销。
              </p>
              <div className="flex justify-center space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteTarget(null);
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  取消
                </button>
                <button
                  onClick={() => handleDelete(deleteTarget._id)}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  删除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 添加备注模态框 */}
      {showRemarkModal && remarkTarget && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 mb-4">添加备注</h3>
              <p className="text-sm text-gray-500 mb-3">
                为「{remarkTarget.name}」的咨询添加备注
              </p>
              <textarea
                value={remarkText}
                onChange={(e) => setRemarkText(e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                placeholder="请输入备注内容..."
              />
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowRemarkModal(false);
                    setRemarkTarget(null);
                    setRemarkText('');
                  }}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  取消
                </button>
                <button
                  onClick={handleAddRemark}
                  disabled={!remarkText.trim()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  添加备注
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default ContactManagement;